import { ContentDetailed, ContentItem, IndexHeaderData } from 'app/shared/models';
import { FormaIconName } from 'forma';
import { InfoPopup } from '../info-popup.model';
import { GraphType } from '../result-pages/index-matrix-graph-value.model';
import { DisplayType } from './menu-item.model';

export interface StyleContainer {
  width?: string;
  height?: string;
  margin?: string;
  gap?: string;
}

export interface ColumnContainer {
  id?: string;
  style?: StyleContainer;
  class?: string[];
  isExclusiveRender?: boolean;
  reportPageItemType?: ReportPageItemType;
  itemKey?: string;
  rows?: RowContainer[];
  matrix?: boolean;
  graph?: boolean;
  graphHeight?: number;
  graphType?: GraphType;
  /**
   * X-axis label format for trend graphs: a full 'date' (default), just the 'month' name, or
   * a compact 'year-month' (e.g. "Okt 2026") for longer trends.
   */
  dateFormat?: 'date' | 'month' | 'year-month';
  indexSwitch?: boolean;
  link?: boolean;
  compact?: boolean;
  useFormaCard?: boolean;
  showTitle?: boolean;
  showComparison?: boolean;
  showDataPoints?: boolean;
  matrixSize?: string;
  titleKey?: string;
  subtitleKey?: string;
  useOldLayout?: boolean;
  surveyInsightLight?: boolean;
  heading?: string;
  content?: string;
  secondaryAction?: string;
  secondaryActionRoutePath?: string[];
  secondaryActionUrl?: string;
  primaryAction?: string;
  primaryActionRoutePath?: string[];
  primaryActionUrl?: string;
  icon?: { name: FormaIconName; color: string; height: string; width: string };
  imageSrc?: string;
  imageMaxHeight?: string;
  backgroundColor?: string;
  settings: any;
  illustration?: string;
}
export interface RowContainer {
  key?: string;
  columns: ColumnContainer[];
  rows: RowContainer[];
  style?: StyleContainer;
  class?: string[];
  condition?: ConditionDto;
  dataSourceNames?: string[];
}
export interface LayoutContainer {
  class?: string[];
  style?: StyleContainer;
  rows: RowContainer[];
}
export interface ReportDefinitionSimple {
  id: number;
  name: string;
  reportKey: string;
  companyId: number;
  parameterSelectors: ReportParameterSelectorDto[];
  parameterKeys?: string[];
  permission?: string;
  type: ReportDefinitionType;
}

export interface ReportPage {
  name: ContentItem[];
  reportGroupSelectorParameter: string;
  items: ReportPageItem[];
  indexHeader: IndexHeaderData;
  collapseQuestions: boolean;
  layoutContainer: string;
}
export interface ReportPageItem<T = any> {
  id: number;
  key: string;
  name: string;
  pageItemType: ReportPageItemType;
  model: T;
  dataSourceParameterValues: string[];
  helpTexts: InfoPopup[];
}

export interface CreateReportDefinitionDto {
  companyId: number;
  reportKey: string;
  templateName: string;
  name: { language: string; text: string }[];
  type: ReportDefinitionType;
  reportPages: ReportPageDto[];
  parameters: ParameterDto[];
  dataSources: DataSourceDto[];
  parameterSelectors: ReportParameterSelectorDto[];
}
export interface ReportDefinitionDto {
  id?: number;
  companyId: number;
  reportKey: string;
  templateName: string;
  type: ReportDefinitionType;
  name: ContentDetailed;
  description: ContentDetailed;
  reportPages: ReportPageDto[];
  parameterSelectors: ReportParameterSelectorDto[];
}

export enum ReportDefinitionType {
  Presentation = 'Presentation',
  Portal = 'Portal',
}
export interface ReportParameterSelectorDto {
  parameterKey: string;
  label?: ContentItem[];
  description?: ContentItem[];
  type: string;
  source: string;
  path?: string;
  hidden?: boolean;
}

export interface SelectableReportDefinition extends ReportDefinitionSimple {
  checked: boolean;
}

export interface ReportStructureDto {
  companyId: number;
  reportPages: ReportPageDto[];
  dataSources: DataSourceDto[];
}

export interface ReportPageDto {
  name: ContentItem[];
  key: string;
  groupName?: string;
  enabled?: boolean;
  reportGroupSelectorSettings?: any;
  layout?: string;
  settings: string;
  pageType?: string;
  condition?: ConditionDto;
  parameter?: string;
  displayType: DisplayType;
  dataConditions?: DataConditionDto[];
  dataSourceNames?: string[];
  meta: ReportPageMetaDto[] | null;
  children?: ReportPageDto[];
  reportPageItems?: ReportPageItemDto[];
}
export interface ReportPageMetaDto {
  key: string;
  value: string;
}
export interface ReportPageItemDto {
  name?: string;
  key?: string;
  enabled: boolean;
  order: number;
  reportPageItemType: ReportPageItemType;
  condition?: ConditionDto;
  dataSourceNames: string[];
  helpTextContentNames: string[];
  settings: ReportPageItemSettings;
}

export interface ReportPageItemSettings {
  indexes?: (
    | string
    | {
        Key: string;
        SubResultKey: string;
      }
  )[];
  indexKey?: string;
  optionGrade?: string;
  expandAllCombinations?: boolean;
  excludedQuestionKeys?: string[];
  comparisonIndexResultValueParam?: string;
}

export interface ReportPageParameterDto {
  name: string;
  dataSourceName: string;
  dataConditions: DataConditionDto[];
}
export interface ConditionDto {
  id?: number;
  name?: string;
  leftSource: string;
  leftSourceId?: number;
  leftField?: string;
  leftDataSourceName?: string;
  leftValue?: string;
  operator: string;
  logic?: string;
  rightSource: string;
  rightSourceId?: number;
  rightField?: string;
  rightDataSourceName?: string;
  rightValue?: string;
  conditions?: ConditionDto[];
}

export interface DataConditionGroup {
  name?: string;
  groupField?: string;
  labels?: string;
  dataLabel?: string;
  dataFields?: string;
  orderBy: string;
  condition: string;
  groups: DataConditionGroup[];
}

export interface DataConditionDto {
  name: string;
  dataSourceType: string;
  groups: DataConditionGroup[];
  condition?: string;
}

export interface DataSourceDto {
  id?: number;
  name: string;
  items: DataSourceItemDto[];
  take: number;
}
export interface DataSourceItemDto {
  id?: number;
  parameterKeys: string[];
  type: string;
}
export interface ParameterDto {
  id?: number;
  key: string;
  initialization: string;
  entityType: string;
  description: string;
  expiration: string;
  expression?: string;
  allowedValues?: string;
  defaultValue?: string;
  readOnly?: boolean;
  allowEmpty?: boolean;
}

export enum ReportPageItemType {
  ENps = 'ENps',
  EngagementMatrix = 'EngagementMatrix',
  RecommendedFocusArea = 'RecommendedFocusArea',
  ENpsDistribution = 'ENpsDistribution',
  ENpsAmbassadors = 'ENpsAmbassadors',
  Information = 'Information',
  ENpsFreetext = 'ENpsFreetext',
  QuestionResults = 'QuestionResults',
  QuestionsWithoutIndex = 'QuestionsWithoutIndex',
  IndexWithComparison = 'IndexWithComparison',
  ResultList = 'ResultList',
  IndexDescription = 'IndexDescription',
  IndexSelector = 'IndexSelector',
  IndexGroupDistributions = 'IndexGroupDistributions',
  BarInsight = 'BarInsight',
  DistributionBar = 'DistributionBar',
  LatestSummary = 'LatestSummary',
  LatestSummaryHeader = 'LatestSummaryHeader',
  PreviousOverviewResults = 'PreviousOverviewResults',
  ParameterSelector = 'ParameterSelector',
  FollowUp = 'FollowUp',
  Recommendations = 'Recommendations',
  RecommendationsFollowup = 'RecommendationsFollowup',
  FollowUpsOverview = 'FollowUpsOverview',
  FollowUpsOverviewTeams = 'FollowUpsOverviewTeams',
  FollowUpsOverviewSummations = 'FollowUpsOverviewSummations',
  ActionPlanList = 'ActionPlanList',
  RecommendationsFollowupSummations = 'RecommendationsFollowupSummations',
  FilterBar = 'FilterBar',
  Description = 'Description',
  ActionPlansOverview = 'ActionPlansOverview',
  RecommendationsStats = 'RecommendationsStats',
  ActionPlanSummaryList = 'ActionPlanSummaryList',
  ActionPlansAreas = 'ActionPlansAreas',
  TopBottomQuestions = 'TopBottomQuestions',
  Perspectives = 'Perspectives',
  PerspectivesFreetext = 'PerspectivesFreetext',
  Act = 'Act',
  ReportSummaryHeader = 'ReportSummaryHeader',
  Nps = 'Nps',
  SummaryFreeText = 'SummaryFreeText',
  SurveyInsight = 'SurveyInsight',
  QuestionTonalityResults = 'QuestionTonalityResults',
  FreetextTonalityResponses = 'FreetextTonalityResponses',
  NpsCorrelation = 'NpsCorrelation',
  TrendGraph = 'TrendGraph',
  CurrentSurveyStatus = 'CurrentSurveyStatus',
  CurrentSurveyStats = 'CurrentSurveyStats',
  IndexValue = 'IndexValue',
  IndexValueMatrix = 'IndexValueMatrix',
  IndexValueMatrixGraph = 'IndexValueMatrixGraph',
  Activities = 'Activities',
  Areas = 'Areas',
  AreasAggregated = 'AreasAggregated',
  CurrentSurveyProgress = 'CurrentSurveyProgress',
  QuestionResultsButton = 'QuestionResultsButton',
  EmptyStateCard = 'EmptyStateCard',
  PendingStateCard = 'PendingStateCard',
  CustomPageItem = 'CustomPageItem',
  WelcomeArea = 'WelcomeArea',
  ResponseRate = 'ResponseRate',
  Drivers = 'Drivers',
  EmptyStateCards = 'EmptyStateCards',
  Title = 'Title',
  IndexValueTrendGraph = 'IndexValueTrendGraph',
  ResultInsight = 'ResultInsight',
  AggTeamResultsGrid = 'AggTeamResultsGrid',
  TextIcon = 'TextIcon',
  Onboarding = 'Onboarding',
  EngagementAggregated = 'EngagementAggregated',
  AreasSpider = 'AreasSpider',
  Coach = 'Coach',
  ReportExport = 'ReportExport',
}
